import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { MemberSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { ClickableImage, ID_PLACEHOLDER, SELFIE_PLACEHOLDER } from './ImagePreview';
import { MerchantServicesManager } from './MerchantServicesManager';
import { BusinessLogoField } from './BusinessLogoField';
import { MerchantImportDialog } from './MerchantImportDialog';
import {
  Users, Search, CheckCircle, XCircle, Clock, Eye, ChevronLeft, ChevronRight,
  Loader2, Store, Mail, Phone, MapPin, Building2, Calendar, Copy, Link2,
  BarChart3, ShoppingBag, Edit, Trash2, TrendingUp, Package, AlertTriangle,
  Download, FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', label: 'Pending Review' },
  approved: { bg: 'bg-[#00E676]/10', text: 'text-[#00E676]', border: 'border-[#00E676]/20', label: 'Approved' },
  rejected: { bg: 'bg-[#FF1744]/10', text: 'text-[#FF1744]', border: 'border-[#FF1744]/20', label: 'Rejected' },
};

const BUSINESS_TYPES = {
  food: 'Food & Beverage', retail: 'Retail & Shopping', health: 'Health & Wellness',
  beauty: 'Beauty & Salon', tech: 'Technology & IT', travel: 'Travel & Tourism',
  education: 'Education & Training', entertainment: 'Entertainment', automotive: 'Automotive',
  real_estate: 'Real Estate', professional: 'Professional Services', other: 'Other',
};

const FILTER_ACTIVE_STYLES = {
  amber: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
  emerald: 'bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30',
  red: 'bg-[#FF1744]/20 text-[#FF1744] border border-[#FF1744]/30',
  blue: 'bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/30',
};
const FILTER_INACTIVE = 'bg-white/5 text-[#A0B4CC] border border-white/10 hover:bg-white/10';

export function MRODashboard() {
  const { user, api } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const limit = 15;

  const fetchDashboard = useCallback(async () => {
    try {
      const { data } = await api.get('/api/mro/dashboard');
      setDashboard(data);
    } catch (e) {
      console.error('MRO dashboard fetch failed:', e);
    }
  }, [api]);

  const fetchMerchants = useCallback(async () => {
    setMerchantsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit, status: statusFilter });
      if (search) params.append('search', search);
      const { data } = await api.get(`/api/mro/merchants?${params}`);
      setMerchants(data.merchants || []);
      setTotal(data.total || 0);
      setCounts(data.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (e) {
      toast.error('Failed to load merchants');
    }
    setMerchantsLoading(false);
  }, [api, page, statusFilter, search]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchDashboard();
      setLoading(false);
    };
    init();
  }, [fetchDashboard]);

  useEffect(() => {
    if (!loading) fetchMerchants();
  }, [loading, fetchMerchants]);

  const openDetail = (m) => {
    setSelectedMerchant(m);
    setDetailOpen(true);
  };

  const handleApprove = async (id) => {
    setActionLoading(true);
    try {
      await api.post(`/api/mro/merchants/${id}/approve`);
      toast.success('Merchant approved');
      setDetailOpen(false);
      fetchMerchants();
      fetchDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    }
    setActionLoading(false);
  };

  const handleReject = async () => {
    if (!selectedMerchant) return;
    setActionLoading(true);
    try {
      await api.post(`/api/mro/merchants/${selectedMerchant.id}/reject`, { reason: rejectReason || 'Application did not meet requirements.' });
      toast.success('Merchant rejected');
      setRejectOpen(false);
      setDetailOpen(false);
      setRejectReason('');
      fetchMerchants();
      fetchDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    }
    setActionLoading(false);
  };

  const copyRecruitUrl = () => {
    const url = `${window.location.origin}/register/merchant?mro=${dashboard?.mro_code || ''}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Recruitment URL copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const openEdit = () => {
    if (!selectedMerchant) return;
    const i = selectedMerchant.merchant_info || {};
    setEditForm({
      name: selectedMerchant.name || '',
      email: selectedMerchant.email || '',
      mobile: selectedMerchant.mobile || '',
      business_name: i.business_name || '',
      business_address: i.business_address || '',
      business_description: i.business_description || '',
      business_logo_url: i.business_logo_url || '',
    });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    setActionLoading(true);
    try {
      await api.put(`/api/mro/merchants/${selectedMerchant.id}`, editForm);
      toast.success('Merchant updated');
      setEditOpen(false);
      setDetailOpen(false);
      fetchMerchants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
    setActionLoading(false);
  };

  const downloadReport = async (format) => {
    try {
      const res = await api.get(`/api/mro/performance-report/${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `MRO_Report_${dashboard?.mro_code || ''}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} report downloaded`);
    } catch (e) {
      toast.error('Failed to download report');
    }
  };

  const totalPages = Math.ceil(total / limit);
  const info = selectedMerchant?.merchant_info || {};
  const sty = STATUS_STYLES[selectedMerchant?.merchant_status] || STATUS_STYLES.pending;
  const stats = dashboard?.stats || {};

  if (loading) {
    return (
      <MemberSidebarLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </MemberSidebarLayout>
    );
  }

  return (
    <MemberSidebarLayout>
      <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="mro-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-white flex items-center gap-2">
              <Users className="w-6 h-6 text-[#00E5FF]" /> Merchant Relations
            </h1>
            <p className="text-[#A0B4CC] text-sm mt-1">Manage your recruited merchant partners</p>
          </div>
          <div className="flex items-center gap-2">
            <MerchantImportDialog
              api={api}
              urlBase="/api/mro/merchants/import"
              onImported={fetchMerchants}
              testIdPrefix="mro-import"
            />
            <Button onClick={() => downloadReport('csv')} size="sm" variant="ghost" className="text-[#A0B4CC] hover:text-white" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1.5" /> CSV
            </Button>
            <Button onClick={() => downloadReport('pdf')} size="sm" variant="ghost" className="text-[#A0B4CC] hover:text-white" data-testid="export-pdf-btn">
              <FileText className="w-4 h-4 mr-1.5" /> PDF
            </Button>
          </div>
        </div>

        {/* Recruitment URL Card */}
        <Card className="bg-gradient-to-r from-[#00E5FF]/5 to-[#007AFF]/5 border-[#00E5FF]/20 mb-6">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <p className="text-white font-medium text-sm flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-[#00E5FF]" /> Your Recruitment URL
                </p>
                <p className="text-[#A0B4CC] text-xs mt-1 font-mono break-all">
                  {window.location.origin}/register/merchant?mro={dashboard?.mro_code || '...'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#00E5FF] text-xs font-mono bg-[#00E5FF]/10 px-3 py-1.5 rounded-lg border border-[#00E5FF]/20">
                  {dashboard?.mro_code || '—'}
                </span>
                <Button
                  onClick={copyRecruitUrl}
                  size="sm"
                  className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90"
                  data-testid="copy-mro-url-btn"
                >
                  {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span className="ml-1.5">{copied ? 'Copied' : 'Copy'}</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
          {[
            { label: 'Total Recruited', value: stats.total_recruited, icon: Users, color: '#00E5FF' },
            { label: 'Pending', value: stats.pending, icon: Clock, color: '#FFD740' },
            { label: 'Approved', value: stats.approved, icon: CheckCircle, color: '#00E676' },
            { label: 'Rejected', value: stats.rejected, icon: XCircle, color: '#FF1744' },
            { label: 'Total Services', value: stats.total_services, icon: ShoppingBag, color: '#7C4DFF' },
          ].map((s) => (
            <Card key={s.label} className="bg-[#041A3B] border-white/10">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${s.color}15` }}>
                  <s.icon className="w-5 h-5" style={{ color: s.color }} />
                </div>
                <div>
                  <p className="text-[#5C7599] text-[10px] uppercase tracking-wider">{s.label}</p>
                  <p className="text-white text-xl font-bold font-outfit">{s.value || 0}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Commission Summary */}
        <Card className="bg-gradient-to-br from-[#041A3B] to-[#0A254D] border-[#00E676]/30 mb-8" data-testid="mro-commission-card">
          <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-[#A0B4CC] text-sm mb-1">Total Onboarding Bonuses</p>
              <p className="font-outfit text-3xl font-bold text-[#00E676]" data-testid="mro-total-commission">
                {new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 2 }).format(stats.total_commission_earned || 0)}
              </p>
              <p className="text-[#5C7599] text-xs mt-1">
                {stats.commission_count || 0} merchants onboarded · {new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 2 }).format(stats.commission_rate || 50)} per approved merchant
              </p>
            </div>
            <div className="text-right">
              <p className="text-[#A0B4CC] text-xs mb-1">Withdrawable Balance</p>
              <p className="font-mono text-lg text-white">
                {new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 2 }).format(stats.withdrawable_balance || 0)}
              </p>
              <a href="/mro/commissions" className="inline-block mt-2 text-xs text-[#00E5FF] hover:underline" data-testid="view-mro-commissions-link">
                View commission history →
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Filter Pills */}
        <div className="flex flex-wrap gap-3 mb-6">
          {[
            { key: 'all', label: 'All', icon: Store, color: 'blue' },
            { key: 'pending', label: 'Pending', icon: Clock, color: 'amber' },
            { key: 'approved', label: 'Approved', icon: CheckCircle, color: 'emerald' },
            { key: 'rejected', label: 'Rejected', icon: XCircle, color: 'red' },
          ].map(s => (
            <button
              key={s.key}
              onClick={() => { setStatusFilter(s.key); setPage(1); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                statusFilter === s.key ? (FILTER_ACTIVE_STYLES[s.color] || FILTER_ACTIVE_STYLES.blue) : FILTER_INACTIVE
              }`}
              data-testid={`mro-filter-${s.key}`}
            >
              <s.icon className="w-3.5 h-3.5" />
              {s.label}
              {s.key !== 'all' && <span className="ml-1 text-xs opacity-70">({counts[s.key] || 0})</span>}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
          <Input
            placeholder="Search by name, email, or business..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-10 bg-[#041A3B] border-white/10 text-white"
            data-testid="mro-merchant-search"
          />
        </div>

        {/* Merchant List */}
        {merchantsLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
          </div>
        ) : merchants.length === 0 ? (
          <div className="text-center py-20 text-[#5C7599]">
            <Store className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No merchants found</p>
            <p className="text-xs mt-1">Share your recruitment URL to start adding merchant partners</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {merchants.map(m => {
              const mi = m.merchant_info || {};
              const st = STATUS_STYLES[m.merchant_status] || STATUS_STYLES.pending;
              return (
                <Card key={m.id} className="bg-[#041A3B] border-white/10 hover:border-white/20 transition-colors cursor-pointer"
                  onClick={() => openDetail(m)} data-testid={`mro-merchant-card-${m.id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#00E676]/10 flex items-center justify-center flex-shrink-0">
                      <Store className="w-6 h-6 text-[#00E676]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-white font-semibold text-sm">{mi.business_name || 'Unnamed Business'}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${st.bg} ${st.text} ${st.border}`}>
                          {st.label}
                        </span>
                      </div>
                      <p className="text-[#A0B4CC] text-xs mt-0.5">{m.name} &middot; {m.email}</p>
                      <p className="text-[#5C7599] text-[10px] mt-0.5">
                        {BUSINESS_TYPES[mi.business_type] || mi.business_type || 'Unspecified'} &middot; Applied {new Date(m.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Eye className="w-4 h-4 text-[#5C7599] flex-shrink-0" />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 px-1">
            <p className="text-[#5C7599] text-xs">Page {page} of {totalPages} ({total} total)</p>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-[#A0B4CC]">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="text-[#A0B4CC]">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="bg-[#041A3B] border-white/10 max-w-lg max-h-[85vh] overflow-y-auto">
            {selectedMerchant && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center gap-2">
                    <Store className="w-5 h-5 text-[#00E676]" />
                    {info.business_name || 'Merchant Application'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  {/* Status Badge */}
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${sty.bg} ${sty.text} ${sty.border}`}>
                    {selectedMerchant.merchant_status === 'pending' && <Clock className="w-3 h-3" />}
                    {selectedMerchant.merchant_status === 'approved' && <CheckCircle className="w-3 h-3" />}
                    {selectedMerchant.merchant_status === 'rejected' && <XCircle className="w-3 h-3" />}
                    {sty.label}
                  </div>

                  {/* Owner Info */}
                  <div className="p-3 rounded-lg bg-[#000B18] space-y-2">
                    <p className="text-[#5C7599] text-[10px] uppercase tracking-wider font-medium">Owner</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-[#A0B4CC] flex items-center gap-1.5"><Mail className="w-3 h-3" /> {selectedMerchant.email}</span>
                      <span className="text-[#A0B4CC] flex items-center gap-1.5"><Phone className="w-3 h-3" /> {selectedMerchant.mobile || 'N/A'}</span>
                      <span className="text-white col-span-2">{selectedMerchant.name}</span>
                    </div>
                  </div>

                  {/* Business Info */}
                  <div className="p-3 rounded-lg bg-[#000B18] space-y-2">
                    <p className="text-[#5C7599] text-[10px] uppercase tracking-wider font-medium">Business</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-[#00E676]" />
                        <span className="text-white font-medium">{info.business_name}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Store className="w-3.5 h-3.5 text-[#5C7599]" />
                        <span className="text-[#A0B4CC]">{BUSINESS_TYPES[info.business_type] || info.business_type || 'Not specified'}</span>
                      </div>
                      {info.business_address && (
                        <div className="flex items-start gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-[#5C7599] mt-0.5" />
                          <span className="text-[#A0B4CC]">{info.business_address}</span>
                        </div>
                      )}
                      {info.business_description && (
                        <p className="text-[#A0B4CC] text-xs leading-relaxed pl-5">{info.business_description}</p>
                      )}
                    </div>
                  </div>

                  {/* ID & Permit Images */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[#5C7599] text-[10px] uppercase mb-1">Government ID</p>
                      <ClickableImage src={selectedMerchant.id_photo_url} fallbackSrc={ID_PLACEHOLDER} label={`ID Photo — ${selectedMerchant.name || ''}`} alt="ID" className="rounded-lg border border-white/10 w-full h-24 object-cover" />
                    </div>
                    <div>
                      <p className="text-[#5C7599] text-[10px] uppercase mb-1">Selfie</p>
                      <ClickableImage src={selectedMerchant.selfie_url} fallbackSrc={SELFIE_PLACEHOLDER} label={`Selfie — ${selectedMerchant.name || ''}`} alt="Selfie" className="rounded-lg border border-white/10 w-full h-24 object-cover" />
                    </div>
                    {info.business_permit_url && (
                      <div className="col-span-2">
                        <p className="text-[#5C7599] text-[10px] uppercase mb-1">Business Permit</p>
                        <ClickableImage src={info.business_permit_url} label={`Business Permit — ${info.business_name || ''}`} alt="Permit" className="rounded-lg border border-[#00E676]/20 w-full h-32 object-cover" />
                      </div>
                    )}
                  </div>

                  {/* Services manager (Edit/Delete/Create) */}
                  {selectedMerchant.merchant_status === 'approved' && (
                    <div className="p-3 rounded-lg bg-[#000B18] border border-white/5">
                      <MerchantServicesManager
                        api={api}
                        merchantId={selectedMerchant.id}
                        urlPrefix="/api/mro/merchants"
                        testIdPrefix="mro"
                      />
                    </div>
                  )}

                  {/* Rejection reason */}
                  {selectedMerchant.merchant_status === 'rejected' && info.rejection_reason && (
                    <div className="p-3 rounded-lg bg-[#FF1744]/5 border border-[#FF1744]/20">
                      <p className="text-[#FF1744] text-xs font-medium">Rejection Reason:</p>
                      <p className="text-[#A0B4CC] text-sm mt-1">{info.rejection_reason}</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {selectedMerchant.merchant_status === 'pending' && (
                  <DialogFooter className="mt-4 flex gap-2">
                    <Button variant="ghost" onClick={() => setRejectOpen(true)} className="text-[#FF1744] hover:bg-[#FF1744]/10" data-testid="mro-reject-btn">
                      <XCircle className="w-4 h-4 mr-1.5" /> Reject
                    </Button>
                    <Button onClick={() => handleApprove(selectedMerchant.id)} disabled={actionLoading}
                      className="bg-[#00E676] text-[#000B18] hover:bg-[#00E676]/90" data-testid="mro-approve-btn">
                      {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1.5" /> Approve</>}
                    </Button>
                  </DialogFooter>
                )}

                {selectedMerchant.merchant_status === 'approved' && (
                  <DialogFooter className="mt-4 flex gap-2">
                    <Button onClick={openEdit} className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90" data-testid="mro-edit-merchant-btn">
                      <Edit className="w-4 h-4 mr-1.5" /> Edit Merchant
                    </Button>
                  </DialogFooter>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Merchant Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="bg-[#041A3B] border-white/10 max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2"><Edit className="w-5 h-5 text-[#00E5FF]" /> Edit Merchant</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <BusinessLogoField
                api={api}
                value={editForm.business_logo_url || ''}
                onChange={(url) => setEditForm({ ...editForm, business_logo_url: url })}
                testIdPrefix="mro-edit-logo"
              />
              <div>
                <Label className="text-[#A0B4CC] text-xs">Owner Name</Label>
                <Input value={editForm.name || ''} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="bg-[#000B18] border-white/20 text-white" data-testid="mro-edit-name" />
              </div>
              <div>
                <Label className="text-[#A0B4CC] text-xs">Email</Label>
                <Input value={editForm.email || ''} onChange={e => setEditForm({ ...editForm, email: e.target.value })} className="bg-[#000B18] border-white/20 text-white" data-testid="mro-edit-email" />
              </div>
              <div>
                <Label className="text-[#A0B4CC] text-xs">Mobile</Label>
                <Input value={editForm.mobile || ''} onChange={e => setEditForm({ ...editForm, mobile: e.target.value })} className="bg-[#000B18] border-white/20 text-white" />
              </div>
              <div>
                <Label className="text-[#A0B4CC] text-xs">Business Name</Label>
                <Input value={editForm.business_name || ''} onChange={e => setEditForm({ ...editForm, business_name: e.target.value })} className="bg-[#000B18] border-white/20 text-white" data-testid="mro-edit-business-name" />
              </div>
              <div>
                <Label className="text-[#A0B4CC] text-xs">Business Address</Label>
                <Input value={editForm.business_address || ''} onChange={e => setEditForm({ ...editForm, business_address: e.target.value })} className="bg-[#000B18] border-white/20 text-white" />
              </div>
              <div>
                <Label className="text-[#A0B4CC] text-xs">Business Description</Label>
                <textarea value={editForm.business_description || ''} onChange={e => setEditForm({ ...editForm, business_description: e.target.value })}
                  rows={3} className="w-full px-3 py-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm focus:border-[#00E5FF] focus:outline-none resize-none" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEditOpen(false)} className="text-[#A0B4CC]">Cancel</Button>
              <Button onClick={handleEdit} disabled={actionLoading} className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90" data-testid="mro-save-edit-btn">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Save Changes</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <DialogContent className="bg-[#041A3B] border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Reject Merchant Application</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Label className="text-[#A0B4CC] text-sm">Reason for rejection</Label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={3}
                placeholder="Provide a reason..."
                className="w-full px-3 py-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm placeholder:text-[#5C7599] focus:border-[#FF1744] focus:outline-none resize-none"
                data-testid="mro-reject-reason"
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setRejectOpen(false)} className="text-[#A0B4CC]">Cancel</Button>
              <Button onClick={handleReject} disabled={actionLoading} className="bg-[#FF1744] text-white hover:bg-[#FF1744]/90" data-testid="mro-confirm-reject-btn">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Rejection'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MemberSidebarLayout>
  );
}
